import { Outlet, useLocation, useNavigate } from 'react-router-dom';
// @mui
import { styled, alpha } from '@mui/material/styles';
import { Box, Badge, Typography, Stack } from '@mui/material';
// contexts
import { useCart } from '../contexts/CartContext';
// utils
import { haptic } from '../utils/telegram';
// components
import Iconify from '../components/Iconify';

// ----------------------------------------------------------------------

export const BOTTOM_NAV_HEIGHT = 64;
export const APP_MAX_WIDTH = 480;

const NAV_ITEMS = [
  { path: '/menu', label: 'Меню', icon: 'mdi:silverware-fork-knife', activeIcon: 'mdi:silverware-variant' },
  { path: '/packages', label: 'Пакеты', icon: 'mdi:package-variant-closed', activeIcon: 'mdi:package-variant' },
  { path: '/cart', label: 'Корзина', icon: 'mdi:cart-outline', activeIcon: 'mdi:cart', isCart: true },
  { path: '/orders', label: 'Заказы', icon: 'mdi:receipt-text-outline', activeIcon: 'mdi:receipt-text' },
  { path: '/profile', label: 'Профиль', icon: 'mdi:account-outline', activeIcon: 'mdi:account' },
];

const Shell = styled('div')(({ theme }) => ({
  minHeight: '100vh',
  maxWidth: APP_MAX_WIDTH,
  margin: '0 auto',
  position: 'relative',
  background: theme.palette.background.default,
  boxShadow: `0 0 0 1px ${alpha(theme.palette.grey[500], 0.12)}`,
  [theme.breakpoints.up('sm')]: {
    minHeight: '100vh',
  },
}));

const Content = styled('main')({
  paddingBottom: BOTTOM_NAV_HEIGHT + 24,
  minHeight: '100vh',
});

const BottomBar = styled('nav')(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  left: '50%',
  transform: 'translateX(-50%)',
  width: '100%',
  maxWidth: APP_MAX_WIDTH,
  height: BOTTOM_NAV_HEIGHT,
  display: 'flex',
  alignItems: 'stretch',
  zIndex: 1200,
  background: alpha(theme.palette.background.paper, 0.92),
  backdropFilter: 'blur(12px)',
  borderTop: `1px solid ${theme.palette.divider}`,
  paddingBottom: 'env(safe-area-inset-bottom)',
}));

// ----------------------------------------------------------------------

export default function MobileLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { count } = useCart();

  const isActive = (path) => location.pathname.startsWith(path);

  return (
    <Shell>
      <Content>
        <Outlet />
      </Content>

      <BottomBar>
        {NAV_ITEMS.map((item) => {
          const active = isActive(item.path);
          return (
            <Box
              key={item.path}
              onClick={() => {
                haptic('light');
                navigate(item.path);
              }}
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.25,
                cursor: 'pointer',
                position: 'relative',
                color: active ? 'primary.main' : 'text.disabled',
                transition: 'color .15s',
                WebkitTapHighlightColor: 'transparent',
                '&:active': { transform: 'scale(0.94)' },
              }}
            >
              {active && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    width: 28,
                    height: 3,
                    borderRadius: '0 0 4px 4px',
                    bgcolor: 'primary.main',
                  }}
                />
              )}
              {item.isCart && count > 0 ? (
                <Badge
                  badgeContent={count}
                  color="error"
                  sx={{ '& .MuiBadge-badge': { fontSize: 10, height: 16, minWidth: 16 } }}
                >
                  <Iconify icon={active ? item.activeIcon : item.icon} width={24} />
                </Badge>
              ) : (
                <Iconify icon={active ? item.activeIcon : item.icon} width={24} />
              )}
              <Typography variant="caption" sx={{ fontSize: 10.5, fontWeight: active ? 700 : 500 }}>
                {item.label}
              </Typography>
            </Box>
          );
        })}
      </BottomBar>
    </Shell>
  );
}

// ----------------------------------------------------------------------

export function MobileHeader({ title, subtitle, onBack, action, sticky = true }) {
  const navigate = useNavigate();
  return (
    <Box
      sx={{
        position: sticky ? 'sticky' : 'relative',
        top: 0,
        zIndex: 1100,
        px: 2,
        py: 1.5,
        bgcolor: (t) => alpha(t.palette.background.paper, 0.92),
        backdropFilter: 'blur(12px)',
        borderBottom: (t) => `1px solid ${t.palette.divider}`,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5}>
        {onBack !== false && (
          <Box
            onClick={() => {
              haptic('light');
              if (onBack) onBack();
              else navigate(-1);
            }}
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'background.neutral',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            <Iconify icon="eva:arrow-ios-back-fill" width={22} />
          </Box>
        )}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="h6" noWrap>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
              {subtitle}
            </Typography>
          )}
        </Box>
        {action}
      </Stack>
    </Box>
  );
}
